import { ArrowRight } from "lucide-react";
import { useSite } from "@/hooks/use-site";
import { localizeUrl } from "@/lib/site";
import tv5Img from "@/assets/products/tv5/ports-closeup-clean.jpg";
import tv5UltraImg from "@/assets/products/tv5-ultra/hero.jpg";
import tv6Img from "@/assets/products/tv6/ports-closeup.jpg";
import tv6UltraImg from "@/assets/products/tv6-ultra/home-hardware-packshot.jpg";
import alcatrazImg from "@/assets/alcatraz/dashboard-dark.png";
import type { Messages } from "@/lib/i18n";

type ProductId = "tv5" | "tv5-ultra" | "tv6" | "tv6-ultra" | "alcatraz" | "alcatraz-start";

type CtaConfig = {
  title: string;
  eyebrow: string;
  subtitle: string;
  url: string;
  img: string;
  imagePosition: string;
  ctaLabel?: string;
};

function getCtaConfig(id: ProductId, messages: Messages): CtaConfig | null {
  switch (id) {
    case "tv5":
      return {
        title: "inext TV5",
        eyebrow: messages.tv5HeroEyebrow,
        subtitle: messages.tv5HeroHeadline,
        url: "/products/inext-tv5",
        img: tv5Img,
        imagePosition: "object-center",
      };
    case "tv5-ultra":
      return {
        title: "inext TV5 Ultra",
        eyebrow: messages.tv5UltraHeroEyebrow,
        subtitle: messages.tv5UltraHeroHeadline,
        url: "/products/inext-tv5-ultra",
        img: tv5UltraImg,
        imagePosition: "object-[50%_70%]",
      };
    case "tv6":
      return {
        title: "inext TV6",
        eyebrow: messages.tv6HeroEyebrow,
        subtitle: messages.tv6HeroHeadline,
        url: "/products/inext-tv6",
        img: tv6Img,
        imagePosition: "object-center",
      };
    case "tv6-ultra":
      return {
        title: "inext TV6 Ultra",
        eyebrow: messages.tv6UltraV2HeroEyebrow,
        subtitle: messages.tv6UltraV2HeroHeadline,
        url: "/products/inext-tv6-ultra",
        img: tv6UltraImg,
        imagePosition: "object-center",
      };
    case "alcatraz":
      return {
        title: "Alcatraz DMS",
        eyebrow: messages.alcatrazHeroEyebrow,
        subtitle: messages.alcatrazHeroSub,
        url: "/solutions/alcatraz-dms",
        img: alcatrazImg,
        imagePosition: "object-top",
      };
    case "alcatraz-start":
      return {
        title: messages.alcatrazStartTitle,
        eyebrow: messages.alcatrazStartEyebrow,
        subtitle: messages.alcatrazStartFormIntro,
        url: "/solutions/alcatraz-dms/get-started?source=blog-product-cta",
        img: alcatrazImg,
        imagePosition: "object-top",
        ctaLabel: messages.alcatrazStartSubmit,
      };
    default:
      return null;
  }
}

export function ProductCTA({ id }: { id: string }) {
  const { site, messages, href } = useSite();
  const data = getCtaConfig(id as ProductId, messages);

  if (!data) return null;

  return (
    <div className="not-prose my-10">
      <a
        href={localizeUrl(data.url, site.locale, href)}
        className="group flex flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-[var(--shadow-soft)] transition-all duration-300 hover:-translate-y-0.5 hover:border-primary/25 hover:shadow-lg sm:flex-row no-underline"
      >
        <div className="relative aspect-[16/10] w-full shrink-0 overflow-hidden sm:aspect-auto sm:w-2/5 sm:min-h-[200px]">
          <img
            src={data.img}
            alt=""
            className={`absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03] ${data.imagePosition}`}
          />
        </div>
        <div className="flex flex-col justify-center p-6 sm:w-3/5 sm:p-8">
          <span className="inline-flex w-fit rounded-full bg-accent px-3 py-1 text-xs font-medium text-accent-foreground">
            {data.eyebrow}
          </span>
          <p className="mt-4 text-xl font-semibold tracking-tight text-foreground sm:text-2xl">
            {data.title}
          </p>
          <p className="mt-2 text-sm leading-relaxed text-muted-foreground sm:text-base">
            {data.subtitle}
          </p>
          <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-medium text-primary">
            {data.ctaLabel ?? messages.learnMore}
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </span>
        </div>
      </a>
    </div>
  );
}
